import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { ITopCalendarDropdownRow, TopCalendarMode } from "@ryde/shared";
import { blueGreyMui } from "@ryde/theme";

import { FramerTooltip } from "../../tooltips";
import { TopCalendarDropdownStyled } from "./top-calendar.styled";

interface ITopCalendarDropdown {
  calendarMode: TopCalendarMode;
  onChangeCalendarMode: (calendarMode: TopCalendarMode) => void;
  dropdownItems: ITopCalendarDropdownRow[];
  onCalendarDropdownOpen: () => void;
}

export const TopCalendarDropdown: FC<ITopCalendarDropdown> = ({
  calendarMode,
  onChangeCalendarMode,
  dropdownItems,
  onCalendarDropdownOpen,
}) => {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const handleCalendarChange = useEvent(
    (event: SelectChangeEvent<TopCalendarMode>): void => {
      onChangeCalendarMode(event.target.value as TopCalendarMode);
    },
  );

  const handleSelectOpen = useEvent((): void => {
    setDropdownOpen(true);
    onCalendarDropdownOpen();
  });

  const handleSelectClose = useEvent((e: React.SyntheticEvent): void => {
    e.preventDefault();
    setDropdownOpen(false);
  });

  return (
    <TopCalendarDropdownStyled>
      <FramerTooltip
        content={t("general.SELECT_DATE_RANGE")}
        rtlStrippedBottomLeftPlacement
        tooltipDisabled={dropdownOpen}
      >
        <Select
          value={calendarMode}
          onChange={handleCalendarChange}
          label={t("general.AGE")}
          size="small"
          color="secondary"
          variant="standard"
          open={dropdownOpen}
          onOpen={handleSelectOpen}
          onClose={handleSelectClose}
          sx={{
            minWidth: 100,
            color: blueGreyMui._500,
            fontSize: "13px",
            minHeight: "16px",
            mr: "8px",
          }}
          disableUnderline={true}
        >
          {dropdownItems?.map(x => (
            <MenuItem
              key={x.value}
              value={x.value}
              disabled={x.value === TopCalendarMode.CUSTOM}
              sx={{
                fontSize: "13px",
                paddingInlineStart: "8px",
                minHeight: "18px",
              }}
            >
              {x.label}
            </MenuItem>
          ))}
        </Select>
      </FramerTooltip>
    </TopCalendarDropdownStyled>
  );
};
